import React, { useState } from 'react';
import { updateCustomerStatus, getCustomerStatusLabel, getReservationStatusLabel } from '../../services/customerService';
import { formatDate } from '../../utils/dateFormat';
import '../../css/CustomerDetailPanel.css';

const CustomerDetailPanel = ({ loading, customer, onClose, onStatusUpdate }) => {
  const [statusChanging, setStatusChanging] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showStatusChange, setShowStatusChange] = useState(false);

  const handleStatusChange = async () => {
    if (!selectedStatus) return;

    setStatusChanging(true);
    try {
      await updateCustomerStatus(customer.customer_id, selectedStatus);
      onStatusUpdate();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('상태 변경에 실패했습니다.');
    } finally {
      setStatusChanging(false);
      setShowStatusChange(false);
      setSelectedStatus('');
    }
  };

  const statusOptions = [
    { value: 'ACTIVE', label: '활성' },
    { value: 'INACTIVE', label: '휴면' },
    { value: 'SUSPENDED', label: '정지' },
  ];

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'status-active';
      case 'INACTIVE':
        return 'status-inactive';
      case 'SUSPENDED':
        return 'status-suspended';
      case 'BANNED':
        return 'status-banned';
      default:
        return '';
    }
  };

  const getReservationStatusClass = (status) => {
    switch (status) {
      case 'CONFIRMED':
        return 'res-status-confirmed';
      case 'IN_USE':
        return 'res-status-using';
      case 'COMPLETED':
        return 'res-status-completed';
      case 'CANCELLED':
        return 'res-status-cancelled';
      case 'NO_SHOW':
        return 'res-status-noshow';
      default:
        return '';
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return '-';
    return `${Number(amount).toLocaleString()}원`;
  };

  return (
    <div className="customer-detail-panel">
      <div className="panel-header">
        <h2>고객 상세</h2>
        <button className="close-btn" onClick={onClose} aria-label="닫기">×</button>
      </div>

      {loading ? (
        <div className="panel-loading">로딩 중...</div>
      ) : customer ? (
        <div className="panel-body">
          <div className="detail-section">
            <h3>기본 정보</h3>
            <div className="detail-list">
              <div className="detail-row">
                <span className="label">이름</span>
                <span className="value">{customer.nickname || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="label">이메일</span>
                <span className="value">{customer.email || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="label">전화번호</span>
                <span className="value">{customer.phone || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="label">가입일</span>
                <span className="value">{formatDate(customer.created_at)}</span>
              </div>
              <div className="detail-row">
                <span className="label">최근 접속일</span>
                <span className="value">{formatDate(customer.last_login_at)}</span>
              </div>
              <div className="detail-row">
                <span className="label">상태</span>
                <span className={`status-badge ${getStatusBadgeClass(customer.status)}`}>
                  {getCustomerStatusLabel(customer.status)}
                </span>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="action-btn"
              onClick={() => setShowStatusChange(!showStatusChange)}
            >
              계정상태변경
            </button>
          </div>

          {showStatusChange && (
            <div className="status-change-section">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="status-select"
              >
                <option value="">상태 선택</option>
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                className="confirm-btn"
                onClick={handleStatusChange}
                disabled={!selectedStatus || statusChanging}
              >
                {statusChanging ? '변경 중...' : '변경'}
              </button>
            </div>
          )}

          <div className="detail-section">
            <h3>활동 내역</h3>
            {customer.reservations && customer.reservations.length > 0 ? (
              <table className="activity-table">
                <thead>
                  <tr>
                    <th>예약 날짜</th>
                    <th>매장</th>
                    <th>결제금액</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {customer.reservations.map((res) => (
                    <tr key={res.reservation_id}>
                      <td>{formatDate(res.reservation_date)}</td>
                      <td>{res.store_name}</td>
                      <td>{formatAmount(res.amount)}</td>
                      <td>
                        <span className={`res-status-badge ${getReservationStatusClass(res.status)}`}>
                          {getReservationStatusLabel(res.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-activity">활동 내역이 없습니다.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="panel-error">고객 정보를 불러올 수 없습니다.</div>
      )}
    </div>
  );
};

export default CustomerDetailPanel;
